import { Box, Typography, keyframes, styled } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { Tone } from '../types/dashboard';

/* ── animations ─────────────────────────────────────────────────────────── */

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: none; }
`;

const dotPulse = keyframes`
  0%, 100% { opacity: 1;  transform: scale(1); }
  50%      { opacity: .3; transform: scale(.65); }
`;

/* ── tone resolution ────────────────────────────────────────────────────── */

/** The fill / ink / edge triple for a colour family. */
export interface ToneColors {
  tint: string;
  deep: string;
  border: string;
  solid: string;
}

export const toneColors = (theme: Theme, tone: Tone): ToneColors => {
  const gc = theme.palette.guidedCourse;
  switch (tone) {
    case 'gold':
      return {
        tint: gc.goldTint,
        deep: gc.goldDeep,
        border: gc.goldTintBorder,
        solid: gc.gold,
      };
    case 'green':
      return {
        tint: gc.greenTint,
        deep: gc.greenDeep,
        border: gc.greenTintBorder,
        solid: gc.green,
      };
    case 'purple':
      return {
        tint: gc.purpleTint,
        deep: gc.purpleDeep,
        border: gc.purpleTintBorder,
        solid: gc.purple,
      };
    case 'red':
      return {
        tint: gc.redTint,
        deep: gc.redDeep,
        border: gc.redLight,
        solid: gc.red,
      };
    case 'lock':
      return {
        tint: gc.lockTint,
        deep: gc.muted,
        border: gc.lockTintHover,
        solid: gc.lock,
      };
    default:
      return {
        tint: gc.blueTint,
        deep: gc.blueInk,
        border: gc.blueTintBorder,
        solid: gc.blue,
      };
  }
};

/* ── page scaffolding ───────────────────────────────────────────────────── */

/**
 * Page root. The two corner glows are the design's `body::before` wash —
 * decorative and identical in both themes, so they stay as literal rgba.
 */
export const PageRoot = styled(Box)(({ theme }) => ({
  position: 'relative',
  flex: 1,
  minHeight: '100%',
  width: '100%',
  minWidth: 0,
  // Backstop against a stray wide child dragging the page sideways. `clip`
  // rather than `hidden` because it bounds the x axis without turning this into
  // a scroll container — so vertical card shadows still paint outside it.
  overflowX: 'clip',
  background: theme.palette.guidedCourse.bg,
  fontFamily: theme.palette.guidedCourse.fontBody,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 0,
    background:
      'radial-gradient(720px 420px at 8% -6%, rgba(47,116,208,.10), transparent 62%),' +
      'radial-gradient(620px 380px at 96% 2%, rgba(139,92,246,.08), transparent 64%)',
  },
}));

/**
 * Centred content column. Matches the design's `.shell`.
 *
 * Everything inside stays within this column — nothing bleeds past it, so the
 * left edge of a card lines up with the section heading above it at every
 * breakpoint. `minWidth: 0` lets the scrolling rail shrink instead of forcing
 * the column wider than the viewport.
 */
export const Shell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'flush',
})<{ flush?: boolean }>(({ theme, flush }) => ({
  position: 'relative',
  zIndex: 1,
  maxWidth: 1220,
  minWidth: 0,
  margin: '0 auto',
  padding: '24px 24px 48px',
  animation: `${fadeUp} .38s cubic-bezier(.2,.9,.3,1) both`,
  [theme.breakpoints.down('sm')]: {
    padding: '16px 13px 40px',
  },
  // For a page that is one full-width panel rather than a stack of cards: the
  // panel's own padding is then the only gutter, instead of the column's
  // padding and the panel's padding stacking into a 50px inset on each side.
  ...(flush && {
    padding: '16px 0 40px',
    [theme.breakpoints.down('sm')]: { padding: '10px 0 32px' },
  }),
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

/* ── surfaces ───────────────────────────────────────────────────────────── */

/**
 * Card surface.
 *
 * `tappable` cards are the page's large buttons, so they carry the same lip as
 * the pill controls: a thickened bottom border that collapses on press. Static
 * panels keep the flat 1px outline — a lip on a passive container reads as an
 * affordance that isn't there.
 */
export const PaperCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tappable',
})<{ tappable?: boolean }>(({ theme, tappable }) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.guidedCourse.line}`,
  borderRadius: 20,
  boxShadow: theme.palette.guidedCourse.shadowSm,
  minWidth: 0,
  ...(tappable && {
    cursor: 'pointer',
    borderWidth: 2,
    borderBottomWidth: 5,
    transition:
      'border-color .16s, box-shadow .16s, transform .1s, border-bottom-width .1s',
    '&:hover': {
      borderColor: theme.palette.guidedCourse.blueTintBorder,
      boxShadow: theme.palette.guidedCourse.cardHoverShadow,
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(1px)',
      borderBottomWidth: 2,
      boxShadow: theme.palette.guidedCourse.shadowSm,
    },
  }),
}));

/** Inset panel used for nested rows (course rows, job cards, comp cards). */
export const InsetRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'highlighted',
})<{ tone?: Tone; highlighted?: boolean }>(({
  theme,
  tone = 'blue',
  highlighted,
}) => {
  const colors = toneColors(theme, tone);
  return {
    borderRadius: 16,
    // Unhighlighted rows drop to the page background so they read as a well
    // inside the card rather than another raised surface.
    background: highlighted ? colors.tint : theme.palette.guidedCourse.bg,
    border: `2px solid ${
      highlighted ? colors.border : theme.palette.guidedCourse.line
    }`,
    transition: 'border-color .15s, transform .12s',
    minWidth: 0,
  };
});

/* ── typography ─────────────────────────────────────────────────────────── */

/**
 * The one micro-label style: section eyebrows, card captions, stat labels.
 *
 * These were three near-identical treatments — 11px/900/.14em display,
 * 10.5px/900/.1em display and 9.5px/700/.12em mono — doing the same job at
 * three sizes in two families. Mono is the house voice for micro labels (it is
 * what the hero pill and the marketing eyebrows use), so everything lands here.
 * Callers set only the colour.
 */
export const microLabel = (theme: Theme) => ({
  fontFamily: theme.palette.guidedCourse.fontMono,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '.12em',
  textTransform: 'uppercase' as const,
  lineHeight: 1.2,
});

export const Eyebrow = styled(Typography)(({ theme }) => ({
  ...microLabel(theme),
  color: theme.palette.guidedCourse.muted,
}));

export const H1 = styled(Typography)(({ theme }) => ({
  fontFamily: theme.palette.guidedCourse.fontDisplay,
  fontSize: 30,
  fontWeight: 800,
  letterSpacing: '-.01em',
  lineHeight: 1.14,
  color: theme.palette.guidedCourse.ink,
  [theme.breakpoints.down('sm')]: { fontSize: 24 },
}));

export const H2 = styled(Typography)(({ theme }) => ({
  fontFamily: theme.palette.guidedCourse.fontDisplay,
  fontSize: 19,
  fontWeight: 800,
  color: theme.palette.guidedCourse.ink,
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  minWidth: 0,
}));

export const SubText = styled(Typography)(({ theme }) => ({
  fontSize: 14.5,
  fontWeight: 600,
  lineHeight: 1.45,
  color: theme.palette.guidedCourse.ink2,
}));

export const MetaText = styled(Typography)(({ theme }) => ({
  fontSize: 12.5,
  fontWeight: 600,
  color: theme.palette.guidedCourse.muted,
}));

/** Tabular figures for scores, counts and XP. */
export const Mono = styled(Box)(({ theme }) => ({
  fontFamily: theme.palette.guidedCourse.fontMono,
  fontVariantNumeric: 'tabular-nums',
}));

/* ── section header row ─────────────────────────────────────────────────── */

export const SectionRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 16,
  flexWrap: 'wrap',
});

export const CardHeadRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
  gap: 12,
  minWidth: 0,
});

/* ── controls ───────────────────────────────────────────────────────────── */

/**
 * Chunky pill tab.
 *
 * Both states sit on a lip, so the row reads as one physical set of controls —
 * an unselected tab that lies flat next to a raised one looks disabled rather
 * than merely unpicked.
 *
 * Both states use the *same* elevation mechanism — a `0 3px 0` lip — and
 * identical padding and border widths. Mixing mechanisms is what made the row
 * ragged before: a box-shadow lip paints outside the border box while a
 * thickened bottom border paints inside it, so the filled tab stood 3px taller
 * than its neighbours even though both border boxes measured the same.
 */
const TAB_LIP = 3;

export const FunTab = styled('button', {
  shouldForwardProp: (prop) => prop !== 'on',
})<{ on?: boolean }>(({ theme, on }) => {
  const gc = theme.palette.guidedCourse;
  return {
    fontFamily: gc.fontBody,
    padding: '10px 20px',
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.2,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    // Reserves the lip's height in layout, so a row of tabs occupies the same
    // space whichever one is selected.
    marginBottom: TAB_LIP,
    transition:
      'background .14s, border-color .14s, color .14s, transform .07s, box-shadow .07s',
    color: on ? '#fff' : gc.ink2,
    background: on ? gc.blue : theme.palette.background.paper,
    border: `2px solid ${on ? gc.blue : gc.line}`,
    boxShadow: `0 ${TAB_LIP}px 0 ${on ? gc.blueDeep : gc.line}`,
    '&:hover': on
      ? { filter: 'brightness(1.05)' }
      : {
          background: gc.blueTint,
          borderColor: gc.blueTintBorder,
          color: gc.blueInk,
        },
    '&:active': {
      transform: `translateY(${TAB_LIP}px)`,
      boxShadow: 'none',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      filter: 'none',
      color: '#fff',
      background: gc.lock,
      borderColor: gc.lock,
      boxShadow: `0 ${TAB_LIP}px 0 ${gc.lockDeep}`,
    },
  };
});

/**
 * The signature "candy" button — a solid fill sitting on a coloured lip that
 * collapses on press, so the control reads as physically pushed down.
 */
export const CandyButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'fullWidth',
})<{ tone?: Tone; fullWidth?: boolean }>(({
  theme,
  tone = 'blue',
  fullWidth,
}) => {
  const gc = theme.palette.guidedCourse;
  const colors = toneColors(theme, tone);
  return {
    fontFamily: gc.fontBody,
    position: 'relative',
    border: 'none',
    borderRadius: 18,
    fontWeight: 800,
    fontSize: 15,
    padding: '12px 22px',
    lineHeight: 1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    userSelect: 'none',
    width: fullWidth ? '100%' : 'auto',
    color: tone === 'gold' ? gc.recommendedColor : '#fff',
    background: colors.solid,
    boxShadow: `0 3px 0 ${colors.deep}`,
    transition: 'transform .06s ease, box-shadow .06s ease, filter .15s',
    '&:hover': { filter: 'brightness(1.04)' },
    '&:active': { transform: 'translateY(3px)', boxShadow: 'none' },
    '&:disabled': {
      cursor: 'not-allowed',
      filter: 'none',
      background: gc.lock,
      boxShadow: `0 3px 0 ${gc.lockDeep}`,
      color: '#fff',
    },
  };
});

const GHOST_LIP = 2;

/** Outline button. `light` is the variant that sits on a dark hero. */
export const GhostButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'light' && prop !== 'fullWidth',
})<{ light?: boolean; fullWidth?: boolean }>(({ theme, light, fullWidth }) => {
  const gc = theme.palette.guidedCourse;
  return {
    fontFamily: gc.fontBody,
    borderRadius: 18,
    fontWeight: 800,
    fontSize: 14,
    padding: '10px 18px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : 'auto',
    transition:
      'transform .06s, box-shadow .06s, background .15s, border-color .15s',
    color: light ? '#fff' : gc.blueInk,
    background: light
      ? 'rgba(255,255,255,.08)'
      : theme.palette.background.paper,
    border: `2px solid ${light ? 'rgba(255,255,255,.28)' : gc.line}`,
    // The lip is a shadow, not a thicker bottom border. A border is part of the
    // border box, so growing and shrinking it on press changed the button's
    // rendered height and nudged everything under it — same reason CandyButton
    // and CCardCta draw theirs with box-shadow. Reserved in layout by the
    // margin, so a row of them occupies fixed space.
    boxShadow: `0 ${GHOST_LIP}px 0 ${light ? 'rgba(255,255,255,.28)' : gc.line}`,
    marginBottom: GHOST_LIP,
    '&:hover': {
      background: light ? 'rgba(255,255,255,.16)' : gc.bg,
    },
    '&:active': { transform: `translateY(${GHOST_LIP}px)`, boxShadow: 'none' },
    // Otherwise a disabled ghost is pixel-identical to a live one.
    '&:disabled': {
      cursor: 'not-allowed',
      color: light ? 'rgba(255,255,255,.45)' : gc.muted,
      background: light ? 'rgba(255,255,255,.04)' : gc.bg,
      '&:hover': { background: light ? 'rgba(255,255,255,.04)' : gc.bg },
      '&:active': { transform: 'none' },
    },
  };
});

/**
 * The "more of this list" link that sits beside a card or rail title.
 *
 * One component rather than the same `sx` override repeated on every card, and
 * it owns the touch target: at this font size the button lands around 30px
 * tall, well under the 44px minimum, and every one of these sits on a surface
 * that ships to mobile. The floor only applies to coarse pointers so the
 * desktop rail keeps its compact header.
 */
export const HeadLink = styled(GhostButton)({
  padding: '7px 13px',
  fontSize: 12.5,
  flexShrink: 0,
  '@media (pointer: coarse)': {
    minHeight: 44,
    padding: '7px 16px',
  },
});

/* ── chips ──────────────────────────────────────────────────────────────── */

export const Chip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'onGlass',
})<{ tone?: Tone; onGlass?: boolean }>(({ theme, tone = 'blue', onGlass }) => {
  const colors = toneColors(theme, tone);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 800,
    fontSize: 12.5,
    padding: '6px 12px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    // On the dark hero, chips are a white scrim rather than a tinted fill —
    // the light tints have no contrast against the gradient.
    background: onGlass ? 'rgba(255,255,255,.16)' : colors.tint,
    color: onGlass ? '#fff' : colors.deep,
  };
});

/* ── layout ─────────────────────────────────────────────────────────────── */

/** Main + rail bento. Collapses to one column below 1040px, per the design. */
export const Bento = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'single',
})<{ single?: boolean }>(({ single }) => ({
  display: 'grid',
  gridTemplateColumns: single ? '1fr' : '1fr 360px',
  gap: 16,
  alignItems: 'start',
  '@media (max-width:1040px)': { gridTemplateColumns: '1fr' },
}));

export const BentoCol = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minWidth: 0,
});

/* ── misc ───────────────────────────────────────────────────────────────── */

export const LiveDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: Tone }>(({ theme, tone = 'green' }) => ({
  width: 8,
  height: 8,
  borderRadius: 999,
  flexShrink: 0,
  background: toneColors(theme, tone).solid,
  animation: `${dotPulse} 1.5s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

export const Divider = styled(Box)(({ theme }) => ({
  height: 2,
  borderRadius: 999,
  background: theme.palette.guidedCourse.line,
}));

export const CenterState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '56px 16px',
  textAlign: 'center',
  color: theme.palette.guidedCourse.muted,
}));

/**
 * An XP payout, as one chip.
 *
 * Shared: the missions board, and the skill card's level-up tooltip. That
 * tooltip used to draw the same value as two objects — a 20px circle reading
 * "XP" beside a separate pill holding the number — because XP has no mark of
 * its own the way the credit coin and the gem do. Making the unit part of the
 * label solves that without inventing an icon, and it is the only presentation
 * of XP the product now has.
 *
 * Sized as the missions board needs it: same box model as `StepRail` there —
 * identical height, identical border width — so the two align by construction.
 * An earlier version was a circular coin on a `0 3px 0` lip; box-shadow sits
 * outside the box model, so the coin's visible mass hung three pixels below the
 * bar it was supposed to sit level with, and a bare number in a squashed circle
 * did not say what it was either.
 *
 * `muted` is the claimed state — green rather than gold, because a reward
 * already taken should not keep advertising itself.
 */
export const XpMedal = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'muted',
})<{ muted?: boolean }>(({ theme, muted }) => {
  const g = theme.palette.guidedCourse;
  return {
    flexShrink: 0,
    height: 30,
    padding: '0 12px',
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: g.fontDisplay,
    fontSize: 13.5,
    fontWeight: 900,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    // Dark ink on gold measures 4.66:1. The tooltip's old pills were white on
    // primary.main (3.08:1 at 12px) and white on a #7DB4F5 crest (2.16:1 at
    // 9px), so adopting this chip fixed two AA failures as a side effect.
    color: muted ? '#fff' : g.recommendedColor,
    background: muted
      ? g.green
      : `linear-gradient(180deg, ${g.goldLight}, ${g.gold})`,
    border: `2px solid ${muted ? g.greenDeep : g.goldDeep}`,
  };
});
